import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { JobsTrialStepContainer } from "domains/hire/containers/JobsTrialStep";
import { ResubscribeDialog } from "domains/hire/containers/ResubscribeDialog";
import type { HireGate } from "domains/hire/hireReadiness";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import type { JobsGate } from "domains/hire/jobsPlan";
import { UpdatePaymentMethodDialog } from "domains/home/containers/UpdatePaymentMethod";

type JobsGateDialogsProps = {
  locationId: number;
  /** The gate the person ran into, or null while nothing is open. */
  open: JobsGate | HireGate | null;
  onClose: () => void;
  /** After the payment method saves, so the action the person clicked can carry on. */
  onPaymentSaved?: () => void;
};

/**
 * What each gate opens: the trial step, Resubscribe, or a note to ask an admin; and for a location that can't hire
 * yet, the payment dialog, a note to ask an organization admin, or a note that Qwick is reviewing the location.
 */
export const JobsGateDialogs = ({ locationId, open, onClose, onPaymentSaved }: JobsGateDialogsProps) => {
  const { location, status } = useJobsGateState(locationId);
  return (
    <>
      <Dialog open={open === "trial"} onClose={onClose}>
        <DialogTitle className="sr-only">Start your free trial</DialogTitle>
        <JobsTrialStepContainer
          locationId={locationId}
          submitLabel="Start free trial"
          saveSubmitLabel="Save and start free trial"
          purpose="unlock"
          onStarted={() => Promise.resolve(onClose())}
          onBack={onClose}
        />
      </Dialog>
      {location && status && (
        <ResubscribeDialog
          open={open === "resubscribe"}
          onClose={onClose}
          locationId={locationId}
          locationName={location.name}
          status={status}
        />
      )}
      <Dialog open={open === "askAdmin"} onClose={onClose}>
        <DialogTitle>Ask an admin</DialogTitle>
        <p className="px-6 text-normal text-gray">
          Ask an admin{location && ` at ${location.name}`} to{" "}
          {status?.can_start_trial ? "start the free trial" : "resubscribe"} and see who applied.
        </p>
        <DialogFooter>
          <Button variant="contained" onClick={onClose}>
            Got it
          </Button>
        </DialogFooter>
      </Dialog>
      {/* Mounted only when asked for: it reads the active location, which pages that never hit this gate may not have. */}
      {open === "addPaymentMethod" && (
        <UpdatePaymentMethodDialog
          open
          onClose={onClose}
          onSaved={() => {
            onClose();
            onPaymentSaved?.();
          }}
        />
      )}
      <Dialog open={open === "askOrgAdmin"} onClose={onClose}>
        <DialogTitle>Ask an organization admin</DialogTitle>
        <p className="px-6 text-normal text-gray">
          {location ? `${location.name} is billed to your organization. ` : ""}Ask your organization admin to add a
          payment method, then you can message Pros, schedule interviews and send offers.
        </p>
        <DialogFooter>
          <Button variant="contained" onClick={onClose}>
            Got it
          </Button>
        </DialogFooter>
      </Dialog>
      <Dialog open={open === "pendingApproval"} onClose={onClose}>
        <DialogTitle>We're reviewing your location</DialogTitle>
        <p className="px-6 text-normal text-gray">
          Once {location?.name ?? "your location"} is approved you can message Pros, schedule interviews and send
          offers. It usually takes a business day, and there's nothing you need to do.
        </p>
        <DialogFooter>
          <Button variant="contained" onClick={onClose}>
            Got it
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};
